import { useEffect, useMemo, useState } from 'react';
import { PIECES } from '../../data';
import { PRELUDE_ENTRIES } from '../../data/chorales/corpusIndex';
import { Instance, playlist } from '../../data/corpus';
import { sonataPiece } from '../../data/sonatas';
import { keyName, parseKey } from '../../music/key';
import type { TensionPoint } from '../../music/tension';
import { usePlayer, usePrefs } from '../hooks';
import { buildView } from '../model';
import { Sparkline } from '../components/Sparkline';
import { PlayButton } from '../components/PlayButton';
import { Crumbs } from '../components/Shell';
import { PauseIcon, PlayIcon } from '../components/Icons';
import { requestPlay } from '../intent';

interface Card {
  id: string;
  number: number;
  key: string;
  bpm: number;
  points: TensionPoint[];
  length: number;
  /** The opening, about eight seconds at its own speed, to the end of a bar. */
  opening: Instance;
}

const SECONDS = 8;
const BASE = 60;

function openingOf(id: string, key: string, bpm: number, measures: Array<{ start: number; length: number }>): Instance {
  const want = (bpm / 60) * SECONDS;
  const end = measures.find((m) => m.start + m.length >= want - 1e-6);
  const stop = end ? end.start + end.length : want;
  return { pieceId: id, kind: 'prelude', title: '', catalogue: '', start: 0, end: stop, key: parseKey(key), label: 'opening' };
}

/**
 * The Well-Tempered Clavier, Book I, as Bach laid it out: a prelude in every major and minor key,
 * rising by semitones. Each card is a prelude's tension line; the tour plays every opening in order,
 * each at its own speed.
 */
export function Keys() {
  const { prefs } = usePrefs();
  const { player, position, playing } = usePlayer();
  const [plan, setPlan] = useState<Array<{ id: string; offset: number; length: number }>>([]);
  /** What the player holds: the whole tour, one prelude's opening, or nothing yet. */
  const [loaded, setLoaded] = useState<string | null>(null);

  const cards = useMemo<Card[]>(() => {
    const first = PIECES.bwv846;
    const v = buildView(first);
    const list: Card[] = [
      { id: 'bwv846', number: 1, key: first.homeKey, bpm: first.tempo, points: v.tension.points, length: v.length, opening: openingOf('bwv846', first.homeKey, first.tempo, first.measures) },
    ];
    for (const e of PRELUDE_ENTRIES) {
      const p = sonataPiece(e.id);
      if (!p) continue;
      list.push({
        id: e.id,
        number: Number(e.id.slice(3)) - 845,
        key: e.homeKey,
        bpm: p.tempo,
        points: e.spark.map((val, i) => ({ t: ((i + 0.5) / e.spark.length) * e.length, v: val / 100, event: i })),
        length: e.length,
        opening: openingOf(e.id, e.homeKey, p.tempo, p.measures),
      });
    }
    return list.sort((a, b) => a.number - b.number);
  }, []);

  useEffect(() => player.setTempo((BASE * prefs.tempo) / 66), [player, prefs.tempo]);

  const tour = (list: Card[]) => {
    const p = playlist(
      list.map((c) => c.opening),
      false,
      1.5,
    );
    // Each opening at its own speed: the tempo map changes speed at each new prelude.
    player.setTempoMap(p.segments.map((s, i) => ({ at: s.offset, factor: list[i].bpm / BASE })));
    setPlan(p.segments.map((s, i) => ({ id: list[i].id, offset: s.offset, length: s.length })));
    setLoaded(list.length > 1 ? 'all' : list[0].id);
    player.setNotes(p.notes, p.total);
    void player.play(0);
  };
  // Pause, then play again: the tour goes on from where it stopped (and starts over once it has ended).
  const playTour = () => (loaded === 'all' ? void player.play() : tour(cards));

  const now = playing ? plan.find((s) => position >= s.offset - 1e-6 && position < s.offset + s.length + 1.5) : undefined;
  const nowCard = now ? cards.find((c) => c.id === now.id) : undefined;

  return (
    <div className="keys">
      <Crumbs items={[{ href: '#library', label: 'Pieces' }, { label: 'The 24 keys' }]} />
      <header className="page-head">
        <span className="label">The Well-Tempered Clavier, Book I · 1722</span>
        <h1>Twenty-four preludes, twenty-four keys</h1>
        <p>
          Bach wrote a prelude and fugue in every major and minor key, rising by semitones: C major, C minor, C♯ major, and on to B minor. Hear how each opening sets
          up its own home.
        </p>
        {cards.length < 24 && <p className="hint">This edition has {cards.length} of the 24 preludes.</p>}
      </header>
      <div className="transport" style={{ marginBottom: 14 }}>
        <PlayButton
          playing={playing && loaded === 'all'}
          label={loaded === 'all' && position > 0 && position < player.total - 1e-6 ? 'Go on with the openings' : `Hear all ${cards.length} openings, in order`}
          onPause={() => player.pause()}
          onPlay={playTour}
        />
        <span className="where">{nowCard ? `Now: No. ${nowCard.number}, ${keyName(parseKey(nowCard.key))}` : `About ${Math.round((cards.length * (SECONDS + 1.5)) / 60)} minutes`}</span>
      </div>
      <div className="key-grid">
        {cards.map((c) => {
          const k = parseKey(c.key);
          return (
            <div key={c.id} className="key-card" data-mode={k.mode} data-on={nowCard?.id === c.id}>
              <div className="key-head">
                <span className="key-no">{c.number}</span>
                <span className="key-name">{keyName(k)}</span>
              </div>
              <span className="key-line">
                <Sparkline points={c.points} length={c.length} width={160} height={34} label={`Tension of the prelude in ${keyName(k)}`} />
              </span>
              <div className="key-actions">
                {playing && loaded === c.id ? (
                  <button className="btn quiet" onClick={() => player.pause()} aria-label={`Stop the opening of the prelude in ${keyName(k)}`}>
                    <PauseIcon /> Stop
                  </button>
                ) : (
                  <button className="btn quiet" onClick={() => tour([c])} aria-label={`Hear the opening of the prelude in ${keyName(k)}`}>
                    <PlayIcon /> Opening
                  </button>
                )}
                <a href={`#${c.id}`} onClick={() => requestPlay(c.id)}>
                  Listen →
                </a>
              </div>
            </div>
          );
        })}
      </div>
      <p className="foot">
        Listening speeds are an editorial choice: Bach wrote no tempo marks. Scores: When in Rome corpus; analyses by Mark Gotham and colleagues (CC BY-SA 4.0).
      </p>
    </div>
  );
}
